import type { ActivityStatsResponse, LevelStatsResponse } from '@arrows/shared';
import { formatElapsedTime } from '../game/result';

/** リザルトの比較行。ラベルと値を分けるのは既存の result-stats の組と揃えるため。 */
export interface ComparisonLine {
  readonly label: string;
  readonly value: string;
}

function groupDigits(value: number): string {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * 上位 % は「速かった」と言えるときだけ出す。
 *
 * サーバーの上位 % は直近のクリア記録に対する順位なので、記録が自分だけの
 * レベルでは必ず 100% になる（= 最下位の表示）。母数が薄いレベルでも数 % 単位で
 * 揺れる。そこで母数の下限と上位半分という二つの条件を満たしたときだけ順位を出し、
 * それ以外は中央値＝次に目指すタイムへ落とす。どちらも無いときは null で行ごと非表示。
 */
export const minimumRankedAttempts = 20;
export const rankedPercentileLimit = 50;

export function levelComparisonLine(stats: LevelStatsResponse | null): ComparisonLine | null {
  if (stats === null) return null;
  const percentile = stats.yourPercentile;
  const attempts = stats.attemptCount ?? 0;
  if (typeof percentile === 'number' && Number.isFinite(percentile)
    && percentile > 0 && percentile <= rankedPercentileLimit
    && attempts >= minimumRankedAttempts) {
    return { label: 'みんなの中で', value: `上位 ${Math.round(percentile)}%` };
  }
  const median = stats.clearTimeP50Ms;
  if (typeof median === 'number' && Number.isFinite(median) && median >= 0) {
    return { label: 'みんなの中央値', value: formatElapsedTime(median) };
  }
  return null;
}

/** 0 人はそもそも自分が数えられていない集計なので、出さずに隠す。 */
export function activityLine(stats: ActivityStatsResponse | null): string | null {
  if (stats === null) return null;
  const players = stats.activeUsers24h;
  if (typeof players !== 'number' || !Number.isFinite(players) || players < 1) return null;
  return `直近24時間で ${groupDigits(players)} 人がプレイ`;
}
